import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { FormControl, MenuItem, Select, Tooltip } from '@mui/material'
import { ArrowDropDown } from '@mui/icons-material'
import { AgentMode, canTransitionFrom, isUserSelectableMode } from '@/index'
import { BaseBadge } from './BaseBadge'
import { standardTooltipProps } from '@/common/components/tooltipStyles'

interface AgentModeBadgeProps {
  readonly agentMode: AgentMode
  readonly selectable?: boolean
  readonly onChange?: ((mode: AgentMode) => void) | undefined
  readonly dangerouslySkipPermissions?: boolean | undefined
  readonly autoModeSupported?: boolean | undefined
}

interface BadgeOptionProps {
  readonly mode: AgentMode
}

export const AgentModeBadge: FC<AgentModeBadgeProps> = ({
  agentMode,
  selectable = false,
  onChange,
  dangerouslySkipPermissions,
  autoModeSupported,
}) => {
  const canTransition = canTransitionFrom(agentMode)
  const effectivelySelectable = selectable && canTransition && onChange

  if (!effectivelySelectable) {
    const badge = <BaseBadge mode={agentMode} disabled={!canTransition} />

    if (!canTransition) {
      const tooltipMessage =
        agentMode === AgentMode.UNKNOWN
          ? 'Agent mode unavailable. Waiting for system to resolve.'
          : 'Waiting for user response. Mode selection disabled.'

      return (
        <Tooltip {...standardTooltipProps} title={tooltipMessage}>
          <span>{badge}</span>
        </Tooltip>
      )
    }

    return badge
  }

  return (
    <FormControl size="small">
      <Select
        value={agentMode}
        onChange={(e) => {
          const newMode = e.target.value as AgentMode
          onChange(newMode)
        }}
        IconComponent={() => null}
        renderValue={(value) => {
          const mode = value as AgentMode
          return (
            <BaseBadge
              mode={mode}
              showBorder={false}
              icon={<ArrowDropDown sx={dropdownIconSx} />}
            />
          )
        }}
        sx={selectSx}
      >
        {Object.values(AgentMode)
          .filter(
            (mode): mode is AgentMode =>
              typeof mode === 'number' &&
              mode !== agentMode &&
              isUserSelectableMode(mode, dangerouslySkipPermissions, autoModeSupported),
          )
          .map((mode) => (
            <MenuItem key={mode} value={mode}>
              <BadgeOption mode={mode} />
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  )
}

const BadgeOption: FC<BadgeOptionProps> = ({ mode }) => <BaseBadge mode={mode} fullWidth />

const selectSx: SxProps<Theme> = {
  minWidth: 180,
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiSelect-select': {
    p: 0,
  },
}

const dropdownIconSx: SxProps<Theme> = {
  fontSize: '1.2rem',
}
